import { useInterpret, useSelector } from '@xstate/react'
import { ParseKeys } from 'i18next'
import { createContext, useContext, useLayoutEffect } from 'react'
import { createMachine, InterpreterFrom, StateFrom } from 'xstate'
import { RentalContractAttributes } from '~/shared/api'
import { RentalContractStatusEnum } from '~/shared/config/enums'

type StateMeta = {
  label: ParseKeys
  color: string
  gridFilter?: boolean
}

type StateConfig = {
  [K in RentalContractStatusEnum]?: Record<string, string | object> & {
    meta: StateMeta
  }
}

export const rentalContractMachine = createMachine({
  id: 'RentalContract',
  initial: RentalContractStatusEnum.SIGNING,
  states: {
    [RentalContractStatusEnum.SIGNING]: {
      on: {
        signing: {
          target: RentalContractStatusEnum.ACTIVE,
        },
        canceled: {
          target: RentalContractStatusEnum.SIGNING_CANCELLED,
        },
        booked_until_visible: {
          target: RentalContractStatusEnum.SIGNING,
        },
        can_first_day_is_free_edit: {
          target: RentalContractStatusEnum.SIGNING,
        },
        can_price_edit: {
          target: RentalContractStatusEnum.SIGNING,
        },
        can_rental_services_edit: {
          target: RentalContractStatusEnum.SIGNING,
        },
        can_calculate_rent_per_day: {
          target: RentalContractStatusEnum.SIGNED,
        },
      },
      meta: {
        label: 'status.pending signature',
        color: 'brand',
      },
    },
    [RentalContractStatusEnum.SIGNED]: {
      on: {
        active: {
          target: RentalContractStatusEnum.ACTIVE,
        },
        canceled: {
          target: RentalContractStatusEnum.SIGNING_CANCELLED,
        },
        booked_until_visible: {
          target: RentalContractStatusEnum.SIGNED,
        },
        can_first_day_is_free_edit: {
          target: RentalContractStatusEnum.SIGNED,
        },
        can_calculate_rent_per_day: {
          target: RentalContractStatusEnum.SIGNED,
        },
        can_price_edit: {
          target: RentalContractStatusEnum.SIGNED,
        },
        can_rental_services_edit: {
          target: RentalContractStatusEnum.SIGNED,
        },
      },
      meta: {
        label: 'status.signed',
        color: 'outlinedGreen',
      },
    },
    [RentalContractStatusEnum.ACTIVE]: {
      on: {
        suspend: {
          target: RentalContractStatusEnum.SUSPENDED,
        },
        terminate: {
          target: RentalContractStatusEnum.TERMINATED,
        },
        form_edit: {
          target: RentalContractStatusEnum.ACTIVE,
        },
        can_rental_services_edit: {
          target: RentalContractStatusEnum.ACTIVE,
        },
      },
      meta: {
        label: 'status.active',
        color: 'green',
      },
    },
    [RentalContractStatusEnum.SUSPENDED]: {
      on: {
        continue: {
          target: RentalContractStatusEnum.ACTIVE,
        },
        terminate: {
          target: RentalContractStatusEnum.TERMINATED,
        },
      },
      meta: {
        label: 'status.suspended',
        color: 'yellow',
      },
    },
    [RentalContractStatusEnum.SIGNING_CANCELLED]: {
      meta: {
        label: 'status.annulled',
        color: 'outlinedRed',
      },
    },
    [RentalContractStatusEnum.TERMINATED]: {
      meta: {
        label: 'status.terminated',
        color: 'red',
      },
    },
  } satisfies StateConfig,
  schema: {
    context: {},
    events: {} as
      | { type: 'signing' }
      | { type: 'active' }
      | { type: 'canceled' }
      | { type: 'suspend' }
      | { type: 'terminate' }
      | { type: 'continue' }
      | { type: 'form_edit' }
      | { type: 'can_first_day_is_free_edit' }
      | { type: 'booked_until_visible' }
      | { type: 'can_price_edit' }
      | { type: 'can_rental_services_edit' }
      | { type: 'can_calculate_rent_per_day' },
  },
  context: {},
  predictableActionArguments: true,
  preserveActionOrder: true,
})

export const useRentalContractMachineInterpreter = (
  rentalContractStatusEnum?: RentalContractAttributes['status'],
) => {
  const interpreter = useInterpret(rentalContractMachine)
  useLayoutEffect(() => {
    if (rentalContractStatusEnum) {
      interpreter.start(rentalContractStatusEnum)
    }
    return () => {
      interpreter.stop()
    }
  }, [interpreter, rentalContractStatusEnum])

  return interpreter
}

type MachineContextValue = {
  interpreter: InterpreterFrom<typeof rentalContractMachine>
}
type MachineState = StateFrom<typeof rentalContractMachine>
export const RentalContractMachineContext = createContext<MachineContextValue>(
  {} as MachineContextValue,
)

export const getMeta = (status?: RentalContractStatusEnum): StateMeta => {
  return status
    ? rentalContractMachine.getStateNode(status)?.meta
    : { label: '', color: '' }
}

const getCan = (state: MachineState) => {
  return {
    signing: state.can('signing'),
    active: state.can('active'),
    canceled: state.can('canceled'),
    suspend: state.can('suspend'),
    terminate: state.can('terminate'),
    continue: state.can('continue'),
    formEdit: state.can('form_edit'),
    canFirstDayIsFreeEdit: state.can('can_first_day_is_free_edit'),
    bookedUntilVisible: state.can('booked_until_visible'),
    canPriceEdit: state.can('can_price_edit'),
    canRentalServicesEdit: state.can('can_rental_services_edit'),
    canCalculateRentPerDay: state.can('can_calculate_rent_per_day'),
  }
}

export const useRentalContractMachineContext = () => {
  const machineCtx = useContext(RentalContractMachineContext)

  const currentStatus = useSelector(
    machineCtx.interpreter,
    (state) => state.value,
  ) as RentalContractStatusEnum

  const meta = getMeta(currentStatus)
  const canStatus = useSelector(machineCtx.interpreter, (state) =>
    getCan(state),
  )

  return { canStatus, meta }
}

export const getCanRentalContractMachine = (
  status?: RentalContractStatusEnum,
) => {
  if (!status) return null

  const state = rentalContractMachine.resolveState({
    ...rentalContractMachine.initialState,
    value: status,
  })

  const canStatus = getCan(state)

  return canStatus
}
